import { HelpCircle, Plus, Trash2, X } from 'lucide-react'
import { Tooltip } from '../../../../components/ui/Tooltip'
import { useScopeStore } from '../../store'
import { getFieldById, getGroupById, getSectionById } from '../../schema'
import { useUpsellContext } from '../../hooks/useUpsellContext'
import {
  type DuplicateMatchField,
  type DuplicateMatchMethod,
  type DuplicateMatchStrength,
  type BlocklistDataType,
  type BlocklistAction,
} from '../../types'

const MATCH_FIELD_OPTIONS: { value: DuplicateMatchField; label: string }[] = [
  { value: 'EXTERNAL_REFERENCE', label: 'External Reference' },
  { value: 'DOC_PRIMARY_IDENTIFIER', label: 'Document Primary ID' },
  { value: 'DOC_SECONDARY_IDENTIFIER', label: 'Document Secondary ID' },
  { value: 'DOC_COUNTRY', label: 'Document Country' },
  { value: 'DOC_SUBDIVISION', label: 'Document Subdivision' },
  { value: 'DOC_TYPE', label: 'Document Type' },
  { value: 'PHONE_NUMBER', label: 'Phone Number' },
  { value: 'EMAIL_ADDRESS', label: 'Email Address' },
  { value: 'GIVEN_NAME', label: 'Given Name' },
  { value: 'FAMILY_NAME', label: 'Family Name' },
  { value: 'DATE_OF_BIRTH', label: 'Date of Birth' },
  { value: 'ADDR_NORM_SHORT', label: 'Short Normalised Address' },
  { value: 'ADDR_NORM_LONG', label: 'Long Normalised Address' },
]

const METHOD_OPTIONS: { value: DuplicateMatchMethod; label: string }[] = [
  { value: 'EXACT', label: 'Exact' },
  { value: 'FUZZY', label: 'Fuzzy' },
]

const STRENGTH_OPTIONS: { value: DuplicateMatchStrength; label: string; color: string }[] = [
  { value: 'LOW', label: 'Low', color: 'bg-gray-500/15 text-gray-400 border-gray-500' },
  { value: 'MEDIUM', label: 'Medium', color: 'bg-amber-500/15 text-amber-400 border-amber-500' },
  { value: 'HIGH', label: 'High', color: 'bg-orange-500/15 text-orange-400 border-orange-500' },
  { value: 'VERY_HIGH', label: 'Very High', color: 'bg-red-500/15 text-red-400 border-red-500' },
]

const DEFAULT_DUPLICATE_RULES: {
  name: string
  matchFields: DuplicateMatchField[]
  method: DuplicateMatchMethod
  strength: DuplicateMatchStrength
}[] = [
  {
    name: 'External reference',
    matchFields: ['EXTERNAL_REFERENCE'],
    method: 'EXACT',
    strength: 'VERY_HIGH',
  },
  {
    name: 'Document identifiers',
    matchFields: ['DOC_PRIMARY_IDENTIFIER', 'DOC_SECONDARY_IDENTIFIER', 'DOC_COUNTRY', 'DOC_SUBDIVISION', 'DOC_TYPE'],
    method: 'EXACT',
    strength: 'VERY_HIGH',
  },
  {
    name: 'Phone number',
    matchFields: ['PHONE_NUMBER'],
    method: 'EXACT',
    strength: 'HIGH',
  },
  {
    name: 'Email address',
    matchFields: ['EMAIL_ADDRESS'],
    method: 'EXACT',
    strength: 'MEDIUM',
  },
  {
    name: 'Given + Family name',
    matchFields: ['GIVEN_NAME', 'FAMILY_NAME'],
    method: 'EXACT',
    strength: 'MEDIUM',
  },
  {
    name: 'Given + Family name + Date of birth',
    matchFields: ['GIVEN_NAME', 'FAMILY_NAME', 'DATE_OF_BIRTH'],
    method: 'EXACT',
    strength: 'VERY_HIGH',
  },
  {
    name: 'Given + Family name + Short form normalised address',
    matchFields: ['ADDR_NORM_SHORT', 'GIVEN_NAME', 'FAMILY_NAME'],
    method: 'EXACT',
    strength: 'HIGH',
  },
]

const STRENGTH_BADGE_COLORS: Record<DuplicateMatchStrength, string> = {
  LOW: 'bg-gray-500/15 text-gray-400',
  MEDIUM: 'bg-amber-500/15 text-amber-400',
  HIGH: 'bg-orange-500/15 text-orange-400',
  VERY_HIGH: 'bg-red-500/15 text-red-400',
}

const STRENGTH_LABELS: Record<DuplicateMatchStrength, string> = {
  LOW: 'Low',
  MEDIUM: 'Medium',
  HIGH: 'High',
  VERY_HIGH: 'Very High',
}

const BLOCKLIST_DATA_TYPE_OPTIONS: { value: BlocklistDataType; label: string }[] = [
  { value: 'EMAIL', label: 'Email Address' },
  { value: 'EMAIL_DOMAIN', label: 'Email Domain' },
  { value: 'PHONE_NUMBER', label: 'Phone Number' },
  { value: 'IP_ADDRESS', label: 'IP Address' },
  { value: 'GIVEN_NAME', label: 'Given Name' },
  { value: 'FAMILY_NAME', label: 'Family Name' },
  { value: 'FULL_NAME', label: 'Full Name' },
  { value: 'COUNTRY', label: 'Country' },
  { value: 'DOC_ID_NUMBER', label: 'Document ID Number' },
  { value: 'EXTERNAL_REFERENCE', label: 'External Reference' },
  { value: 'DEVICE_ID', label: 'Device ID' },
  { value: 'OTHER', label: 'Other' },
]

const BLOCKLIST_ACTION_OPTIONS: { value: BlocklistAction; label: string; description: string; color: string }[] = [
  { value: 'BLOCK', label: 'Block', description: 'Stop the onboarding process', color: 'bg-red-500/15 text-red-400 border-red-500' },
  { value: 'ALLOW', label: 'Allow', description: 'Automatically approve', color: 'bg-green-500/15 text-green-400 border-green-500' },
  { value: 'ALERT', label: 'Alert', description: 'Flag but let proceed', color: 'bg-amber-500/15 text-amber-400 border-amber-500' },
  { value: 'REVIEW', label: 'Review', description: 'Send to manual review', color: 'bg-blue-500/15 text-blue-400 border-blue-500' },
  { value: 'NONE', label: 'None', description: 'Risk score only', color: 'bg-gray-500/15 text-gray-400 border-gray-500' },
]

const DUPLICATE_TOOLTIP_V1 = (
  <div className="space-y-3">
    <div>
      <p className="font-semibold mb-1">What is Duplicate Detection?</p>
      <p>Automatically identifies when a new customer may already exist in your system, preventing duplicate accounts and potential fraud.</p>
    </div>
    <div>
      <p className="font-semibold mb-1">How it Works</p>
      <p>During onboarding, key data points (name, DOB, email, phone, ID numbers, address) are compared against existing entities using <strong>exact matching</strong>. All configured attributes must match precisely to flag a potential duplicate.</p>
    </div>
    <div>
      <p className="font-semibold mb-1">When a Duplicate is Detected</p>
      <ul className="list-disc pl-4 space-y-0.5">
        <li>The entity is flagged with a <strong>Duplicate issue</strong></li>
        <li>The entity is sent for <strong>Review</strong> in the portal or via API</li>
        <li>Your team can then resolve whether it's a true duplicate or a false positive</li>
      </ul>
    </div>
    <div>
      <p className="font-semibold mb-1">Configuration</p>
      <p>Use <strong>Default Rules</strong> for standard matching, or define <strong>Custom Rules</strong> to choose exactly which fields to match on for your business needs.</p>
    </div>
  </div>
)

const DUPLICATE_TOOLTIP_V2 = (
  <div className="space-y-3">
    <div>
      <p className="font-semibold mb-1">What is Duplicate Detection?</p>
      <p>Automatically identifies when a new customer may already exist in your system, preventing duplicate accounts and potential fraud.</p>
    </div>
    <div>
      <p className="font-semibold mb-1">How it Works</p>
      <p>During onboarding, key data points are compared against existing entities using <strong>exact</strong> or <strong>fuzzy matching</strong>. Fuzzy matching catches near-matches like nicknames and typos using configurable confidence thresholds.</p>
    </div>
    <div>
      <p className="font-semibold mb-1">When a Duplicate is Detected</p>
      <ul className="list-disc pl-4 space-y-0.5">
        <li>The entity is flagged with a <strong>Duplicate issue</strong></li>
        <li>The entity is sent for <strong>Review</strong> in the portal or via API</li>
        <li>Your team can resolve duplicates as true positives or false positives</li>
      </ul>
    </div>
    <div>
      <p className="font-semibold mb-1">Configuration</p>
      <p>Use <strong>Default Rules</strong> for standard matching, or define <strong>Custom Rules</strong> to choose which fields to match on, the matching method (exact or fuzzy), and the confidence strength for each rule.</p>
    </div>
  </div>
)

const MATCH_LISTS_TOOLTIP = (
  <div className="space-y-2">
    <p className="font-semibold">FrankieOne Match Lists: Your Custom Rules Engine</p>
    <p>Think of Match Lists as your own custom security lists for your business. You create lists of specific data (like emails, names, or countries), and then decide what should happen automatically when a new customer's information matches an entry on your list.</p>
    <ul className="list-disc pl-4 space-y-1">
      <li><strong>Core Rule:</strong> A Match List checks if a new applicant's data exists on a list you've created. If a match is found, FrankieOne will automatically trigger a pre-defined action that you have set for that list.</li>
      <li><strong>Powerful Configuration & Actions:</strong> For each list you create, you can define:
        <ol className="list-decimal pl-4 mt-1 space-y-0.5">
          <li><strong>The Data:</strong> What you want to look for (e.g., email domains, phone numbers, IP addresses, names, countries).</li>
          <li><strong>The Action:</strong> What to do when a match is found — BLOCK, ALLOW, ALERT, REVIEW, or NONE.</li>
          <li><strong>The Risk Score:</strong> A specific risk score (e.g., +50 or -20) to help with your overall risk assessment.</li>
        </ol>
      </li>
    </ul>
    <p className="font-semibold pt-1">Practical Example</p>
    <ul className="list-disc pl-4 space-y-1">
      <li><strong>List 1 "Known Fraudsters":</strong> Data: fraudster@email.com → Action: BLOCK</li>
      <li><strong>List 2 "High-Risk Region":</strong> Data: Country X → Action: NONE, Risk Score: +75</li>
    </ul>
    <p className="text-[10px] opacity-75 pt-1">Match Lists give you the power to create highly specific, automated business rules for managing risk, stopping fraud, and streamlining operations beyond standard identity checks.</p>
  </div>
)

export function AdvancedRulesSection() {
  const {
    scope,
    updateRulesConfig,
    addCustomDuplicateRule,
    updateCustomDuplicateRule,
    removeCustomDuplicateRule,
    addCustomBlocklistEntry,
    updateCustomBlocklistEntry,
    removeCustomBlocklistEntry,
  } = useScopeStore()
  const { rulesConfig } = scope
  const disabled = scope.finalized
  const { mapLabel } = useUpsellContext()
  const isV1 = scope.generalInfo.platformVersion === 'v1'
  const platformVersion = scope.generalInfo?.platformVersion || 'unknown'

  // --- Rules: Duplicate & Blocklist fields ---
  const duplicateSection = getSectionById('rules_duplicate_matching')
  const duplicateGroup = getGroupById('rules_duplicate_group')
  const duplicateModeField = getFieldById('rules_duplicate_rules_mode')
  const blocklistSection = getSectionById('rules_blocklist_matching')
  const blocklistGroup = getGroupById('rules_blocklist_group')
  const blocklistModeField = getFieldById('rules_blocklist_rules_mode')

  return (
    <>
      {/* Duplicate Matching Rules */}
      <div className="pt-2">
        <div className="flex items-center gap-2">
          <h4 className="text-base font-semibold text-[var(--text-primary)]">
            {duplicateSection?.formTitle || duplicateGroup?.formTitle || 'Duplicate Matching Rules'}
          </h4>
          <Tooltip
            content={isV1 ? DUPLICATE_TOOLTIP_V1 : DUPLICATE_TOOLTIP_V2}
            maxWidth={420}
          >
            <HelpCircle size={16} className="text-[var(--text-muted)] hover:text-[var(--text-secondary)]" />
          </Tooltip>
          {platformVersion !== 'unknown' && (
            <span className={`ml-1 px-2 py-0.5 text-xs font-medium rounded-full ${
              isV1 ? 'bg-purple-500/15 text-purple-400' : 'bg-blue-500/15 text-blue-400'
            }`}>
              {platformVersion.toUpperCase()}
            </span>
          )}
        </div>
      </div>
      <div className="ml-3 pl-4 border-l-2 border-[var(--border-color)] space-y-4">
        <p className="text-xs text-[var(--text-muted)] mb-3">
          <span className="text-primary">*</span>{' '}
          {duplicateModeField?.formDescription || duplicateGroup?.helperText || 'Required for completion.'}
        </p>
        <div className="space-y-3 mb-4">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="radio"
              name="duplicateRules"
              className="w-4 h-4 text-primary focus:ring-primary"
              checked={rulesConfig.duplicateRules === 'unknown'}
              onChange={() => updateRulesConfig('duplicateRules', 'unknown')}
              disabled={disabled}
            />
            <span className="text-sm text-[var(--text-primary)]">
              {mapLabel(duplicateModeField?.options?.find((o) => o.value === 'unknown')?.label || "Don't know")}
            </span>
          </label>
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="radio"
              name="duplicateRules"
              className="w-4 h-4 text-primary focus:ring-primary"
              checked={rulesConfig.duplicateRules === 'default'}
              onChange={() => updateRulesConfig('duplicateRules', 'default')}
              disabled={disabled}
            />
            <span className="text-sm text-[var(--text-primary)]">
              {duplicateModeField?.options?.find((o) => o.value === 'default')?.label || 'Use Default Rules'}
            </span>
          </label>
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="radio"
              name="duplicateRules"
              className="w-4 h-4 text-primary focus:ring-primary"
              checked={rulesConfig.duplicateRules === 'custom'}
              onChange={() => updateRulesConfig('duplicateRules', 'custom')}
              disabled={disabled}
            />
            <span className="text-sm text-[var(--text-primary)]">
              {duplicateModeField?.options?.find((o) => o.value === 'custom')?.label || 'Custom Rules'}
            </span>
          </label>
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="radio"
              name="duplicateRules"
              className="w-4 h-4 text-primary focus:ring-primary"
              checked={rulesConfig.duplicateRules === 'not_required'}
              onChange={() => updateRulesConfig('duplicateRules', 'not_required')}
              disabled={disabled}
            />
            <span className="text-sm text-[var(--text-primary)]">
              {duplicateModeField?.options?.find((o) => o.value === 'not_required')?.label || 'Not Required'}
            </span>
          </label>
        </div>

        {rulesConfig.duplicateRules === 'default' ? (
          <div className="mt-4 pt-4 border-t border-[var(--border-color)]">
            <div className="flex items-center justify-between mb-3">
              <h5 className="text-xs text-[var(--text-secondary)]">
                Default Rules — {DEFAULT_DUPLICATE_RULES.length} rules
              </h5>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[var(--border-color)]">
                    <th className="text-left py-2 px-3 text-[var(--text-secondary)] font-medium">Rule Name</th>
                    <th className="text-left py-2 px-3 text-[var(--text-secondary)] font-medium">Match Fields</th>
                    <th className="text-left py-2 px-3 text-[var(--text-secondary)] font-medium">Method</th>
                    {!isV1 && <th className="text-left py-2 px-3 text-[var(--text-secondary)] font-medium">Strength</th>}
                  </tr>
                </thead>
                <tbody>
                  {DEFAULT_DUPLICATE_RULES.map((rule, index) => (
                    <tr key={index} className="border-b border-[var(--border-color)]/50 align-top">
                      <td className="py-2 px-3 text-[var(--text-primary)] font-medium whitespace-nowrap">
                        {rule.name}
                      </td>
                      <td className="py-2 px-3">
                        <div className="flex flex-wrap gap-1">
                          {rule.matchFields.map((f) => {
                            const label = MATCH_FIELD_OPTIONS.find((o) => o.value === f)?.label || f
                            return (
                              <span
                                key={f}
                                className="inline-block px-1.5 py-0.5 text-[10px] rounded bg-[var(--bg-secondary)] text-[var(--text-secondary)] border border-[var(--border-color)]"
                              >
                                {label}
                              </span>
                            )
                          })}
                        </div>
                      </td>
                      <td className="py-2 px-3 text-[var(--text-secondary)]">{rule.method}</td>
                      {!isV1 && (
                        <td className="py-2 px-3">
                          <span className={`inline-flex items-center justify-center min-w-[64px] px-2.5 py-0.5 text-[10px] font-medium rounded-full text-center ${STRENGTH_BADGE_COLORS[rule.strength]}`}>
                            {STRENGTH_LABELS[rule.strength]}
                          </span>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : rulesConfig.duplicateRules === 'not_required' ? null : rulesConfig.duplicateRules === 'custom' ? (
          <div className="mt-4 pt-4 border-t border-[var(--border-color)] space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <h5 className="text-sm font-medium text-[var(--text-primary)]">Custom Duplicate Rules</h5>
                <Tooltip content="Define individual matching rules. Each rule specifies which fields to compare, the matching method (exact or fuzzy), and the match strength that determines how confidently a duplicate is flagged.">
                  <HelpCircle size={14} className="text-[var(--text-muted)] hover:text-[var(--text-secondary)]" />
                </Tooltip>
              </div>
              <button
                type="button"
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-primary text-white hover:bg-primary/90 transition-colors disabled:opacity-50"
                onClick={addCustomDuplicateRule}
                disabled={disabled}
              >
                <Plus size={14} />
                Add Rule
              </button>
            </div>

            {(rulesConfig.customDuplicateRuleSet || []).length === 0 ? (
              <div className="text-center py-8 text-[var(--text-muted)] text-sm border border-dashed border-[var(--border-color)] rounded-lg">
                No custom rules defined yet. Click "Add Rule" to create one.
              </div>
            ) : (
              <div className="space-y-3">
                {(rulesConfig.customDuplicateRuleSet || []).map((rule, idx) => (
                  <div
                    key={rule.id}
                    className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg p-4 space-y-3"
                  >
                    {/* Rule header with delete */}
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-[var(--text-muted)] uppercase tracking-wide">
                        Rule {idx + 1}
                      </span>
                      <button
                        type="button"
                        className="p-1 text-[var(--text-muted)] hover:text-red-400 transition-colors disabled:opacity-50"
                        onClick={() => removeCustomDuplicateRule(idx)}
                        disabled={disabled}
                        title="Remove rule"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>

                    {/* Rule Name */}
                    <div>
                      <label className="block text-xs text-[var(--text-secondary)] mb-1">Rule Name</label>
                      <input
                        type="text"
                        className="w-full h-9 bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-lg px-3 text-sm text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
                        placeholder="e.g. Given + Family name + Date of birth"
                        value={rule.name}
                        onChange={(e) => updateCustomDuplicateRule(idx, { name: e.target.value })}
                        disabled={disabled}
                      />
                    </div>

                    {/* Match Fields */}
                    <div>
                      <label className="block text-xs text-[var(--text-secondary)] mb-1">Match Fields</label>
                      <div className="flex flex-wrap gap-1.5 mb-2">
                        {rule.matchFields.map((field) => {
                          const opt = MATCH_FIELD_OPTIONS.find((o) => o.value === field)
                          return (
                            <span
                              key={field}
                              className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-md bg-primary/10 text-primary border border-primary/20"
                            >
                              {opt?.label || field}
                              {!disabled && (
                                <button
                                  type="button"
                                  className="hover:text-red-400 transition-colors"
                                  onClick={() =>
                                    updateCustomDuplicateRule(idx, {
                                      matchFields: rule.matchFields.filter((f) => f !== field),
                                    })
                                  }
                                >
                                  <X size={12} />
                                </button>
                              )}
                            </span>
                          )
                        })}
                      </div>
                      <select
                        className="w-full h-9 bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-lg px-3 text-sm text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
                        value=""
                        onChange={(e) => {
                          const val = e.target.value as DuplicateMatchField
                          if (val && !rule.matchFields.includes(val)) {
                            updateCustomDuplicateRule(idx, {
                              matchFields: [...rule.matchFields, val],
                            })
                          }
                        }}
                        disabled={disabled}
                      >
                        <option value="">+ Add a match field...</option>
                        {MATCH_FIELD_OPTIONS.filter((o) => !rule.matchFields.includes(o.value)).map(
                          (opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          )
                        )}
                      </select>
                    </div>

                    {/* Method + Strength row */}
                    <div className={`grid gap-3 ${isV1 ? 'grid-cols-1' : 'grid-cols-2'}`}>
                      <div>
                        <label className="block text-xs text-[var(--text-secondary)] mb-1">Method</label>
                        {isV1 && (
                          <p className="text-[10px] text-[var(--text-muted)] mb-1">V1 supports exact matching only</p>
                        )}
                        <div className="flex gap-2">
                          {METHOD_OPTIONS.filter((opt) => !isV1 || opt.value === 'EXACT').map((opt) => (
                            <label
                              key={opt.value}
                              className={`flex-1 text-center px-3 py-1.5 rounded-lg border cursor-pointer text-xs font-medium transition-colors ${
                                rule.method === opt.value
                                  ? 'border-primary bg-primary/10 text-[var(--text-primary)]'
                                  : 'border-[var(--border-color)] text-[var(--text-secondary)] hover:border-gray-500'
                              } ${disabled ? (rule.method === opt.value ? 'cursor-not-allowed' : 'opacity-40 cursor-not-allowed') : ''}`}
                            >
                              <input
                                type="radio"
                                name={`dup-method-${rule.id}`}
                                value={opt.value}
                                checked={rule.method === opt.value}
                                onChange={() =>
                                  updateCustomDuplicateRule(idx, { method: opt.value })
                                }
                                disabled={disabled}
                                className="hidden"
                              />
                              {opt.label}
                            </label>
                          ))}
                        </div>
                      </div>
                      {!isV1 && (
                        <div>
                          <label className="block text-xs text-[var(--text-secondary)] mb-1">Strength</label>
                          <div className="flex gap-1.5">
                            {STRENGTH_OPTIONS.map((opt) => (
                              <label
                                key={opt.value}
                                className={`flex-1 text-center px-1.5 py-1.5 rounded-lg border cursor-pointer text-xs font-medium transition-colors ${
                                  rule.strength === opt.value
                                    ? opt.color
                                    : 'border-[var(--border-color)] text-[var(--text-secondary)] hover:border-gray-500'
                                } ${disabled ? (rule.strength === opt.value ? 'cursor-not-allowed' : 'opacity-40 cursor-not-allowed') : ''}`}
                              >
                                <input
                                  type="radio"
                                  name={`dup-strength-${rule.id}`}
                                  value={opt.value}
                                  checked={rule.strength === opt.value}
                                  onChange={() =>
                                    updateCustomDuplicateRule(idx, { strength: opt.value })
                                  }
                                  disabled={disabled}
                                  className="hidden"
                                />
                                {opt.label}
                              </label>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Additional notes */}
            <div>
              <label className="block text-xs text-[var(--text-secondary)] mb-1">
                Additional Notes
              </label>
              <textarea
                className="w-full h-20 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg px-3 py-2 text-sm text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50 resize-none"
                placeholder="Any additional duplicate customisation requirements..."
                value={rulesConfig.customDuplicateNotes || ''}
                onChange={(e) => updateRulesConfig('customDuplicateNotes', e.target.value)}
                disabled={disabled}
              />
            </div>
          </div>
        ) : null}
      </div>

      {/* Blocklist Matching Rules */}
      <div className="pt-2">
        <div className="flex items-center gap-2">
          <h4 className="text-base font-semibold text-[var(--text-primary)]">
            {blocklistSection?.formTitle || blocklistGroup?.formTitle || 'Blocklist Matching Rules'}
          </h4>
          <Tooltip content={MATCH_LISTS_TOOLTIP} maxWidth={520}>
            <HelpCircle size={16} className="text-[var(--text-muted)] hover:text-[var(--text-secondary)]" />
          </Tooltip>
        </div>
      </div>
      <div className="ml-3 pl-4 border-l-2 border-[var(--border-color)] space-y-4">
        <p className="text-xs text-[var(--text-muted)] mb-3">
          <span className="text-primary">*</span>{' '}
          {blocklistModeField?.formDescription || blocklistGroup?.helperText || 'Required for completion.'}
        </p>
        <div className="space-y-3 mb-4">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="radio"
              name="blocklistRules"
              className="w-4 h-4 text-primary focus:ring-primary"
              checked={rulesConfig.blocklistRules === 'unknown'}
              onChange={() => updateRulesConfig('blocklistRules', 'unknown')}
              disabled={disabled}
            />
            <span className="text-sm text-[var(--text-primary)]">
              {mapLabel(blocklistModeField?.options?.find((o) => o.value === 'unknown')?.label || "Don't know")}
            </span>
          </label>
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="radio"
              name="blocklistRules"
              className="w-4 h-4 text-primary focus:ring-primary"
              checked={rulesConfig.blocklistRules === 'custom'}
              onChange={() => updateRulesConfig('blocklistRules', 'custom')}
              disabled={disabled}
            />
            <span className="text-sm text-[var(--text-primary)]">
              {blocklistModeField?.options?.find((o) => o.value === 'custom')?.label || 'Custom Rules'}
            </span>
          </label>
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="radio"
              name="blocklistRules"
              className="w-4 h-4 text-primary focus:ring-primary"
              checked={rulesConfig.blocklistRules === 'not_required'}
              onChange={() => updateRulesConfig('blocklistRules', 'not_required')}
              disabled={disabled}
            />
            <span className="text-sm text-[var(--text-primary)]">
              {blocklistModeField?.options?.find((o) => o.value === 'not_required')?.label || 'Not Required'}
            </span>
          </label>
        </div>

        {rulesConfig.blocklistRules === 'custom' ? (
          <div className="mt-4 pt-4 border-t border-[var(--border-color)] space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <h5 className="text-sm font-medium text-[var(--text-primary)]">Custom Match Lists</h5>
                <Tooltip content="Define your custom match lists. Each list specifies a data type to match against, the action to take when matched, and an optional risk score adjustment.">
                  <HelpCircle size={14} className="text-[var(--text-muted)] hover:text-[var(--text-secondary)]" />
                </Tooltip>
              </div>
              <button
                type="button"
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-primary text-white hover:bg-primary/90 transition-colors disabled:opacity-50"
                onClick={addCustomBlocklistEntry}
                disabled={disabled}
              >
                <Plus size={14} />
                Add List
              </button>
            </div>

            {(rulesConfig.customBlocklistEntries || []).length === 0 ? (
              <div className="text-center py-8 text-[var(--text-muted)] text-sm border border-dashed border-[var(--border-color)] rounded-lg">
                No custom match lists defined yet. Click "Add List" to create one.
              </div>
            ) : (
              <div className="space-y-3">
                {(rulesConfig.customBlocklistEntries || []).map((entry, idx) => (
                  <div
                    key={entry.id}
                    className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg p-4 space-y-3"
                  >
                    {/* Entry header with delete */}
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-[var(--text-muted)] uppercase tracking-wide">
                        List {idx + 1}
                      </span>
                      <button
                        type="button"
                        className="p-1 text-[var(--text-muted)] hover:text-red-400 transition-colors disabled:opacity-50"
                        onClick={() => removeCustomBlocklistEntry(idx)}
                        disabled={disabled}
                        title="Remove list"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>

                    {/* List Name */}
                    <div>
                      <label className="block text-xs text-[var(--text-secondary)] mb-1">List Name</label>
                      <input
                        type="text"
                        className="w-full h-9 bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-lg px-3 text-sm text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
                        placeholder='e.g. "Known Fraudsters", "High-Risk Regions"'
                        value={entry.name}
                        onChange={(e) => updateCustomBlocklistEntry(idx, { name: e.target.value })}
                        disabled={disabled}
                      />
                    </div>

                    {/* Data Type + Action row */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-[var(--text-secondary)] mb-1">Data Type</label>
                        <select
                          className="w-full h-9 bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-lg px-3 text-sm text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
                          value={entry.dataType}
                          onChange={(e) =>
                            updateCustomBlocklistEntry(idx, { dataType: e.target.value as BlocklistDataType })
                          }
                          disabled={disabled}
                        >
                          {BLOCKLIST_DATA_TYPE_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs text-[var(--text-secondary)] mb-1">Action</label>
                        <div className="flex flex-wrap gap-1.5">
                          {BLOCKLIST_ACTION_OPTIONS.map((opt) => (
                            <label
                              key={opt.value}
                              className={`flex-1 text-center px-1.5 py-1.5 rounded-lg border cursor-pointer text-xs font-medium transition-colors ${
                                entry.action === opt.value
                                  ? opt.color
                                  : 'border-[var(--border-color)] text-[var(--text-secondary)] hover:border-gray-500'
                              } ${disabled ? (entry.action === opt.value ? 'cursor-not-allowed' : 'opacity-40 cursor-not-allowed') : ''}`}
                              title={opt.description}
                            >
                              <input
                                type="radio"
                                name={`bl-action-${entry.id}`}
                                value={opt.value}
                                checked={entry.action === opt.value}
                                onChange={() =>
                                  updateCustomBlocklistEntry(idx, { action: opt.value })
                                }
                                disabled={disabled}
                                className="hidden"
                              />
                              {opt.label}
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Risk Score */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-[var(--text-secondary)] mb-1">
                          Risk Score Adjustment
                          <span className="text-[var(--text-muted)] ml-1">(optional)</span>
                        </label>
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            className="w-24 h-9 bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-lg px-3 text-sm text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
                            placeholder="e.g. +75"
                            value={entry.riskScore ?? ''}
                            onChange={(e) =>
                              updateCustomBlocklistEntry(idx, {
                                riskScore: e.target.value === '' ? null : Number(e.target.value),
                              })
                            }
                            disabled={disabled}
                          />
                          <span className="text-xs text-[var(--text-muted)]">points</span>
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs text-[var(--text-secondary)] mb-1">
                          Description
                          <span className="text-[var(--text-muted)] ml-1">(optional)</span>
                        </label>
                        <input
                          type="text"
                          className="w-full h-9 bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-lg px-3 text-sm text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
                          placeholder="What this list is for..."
                          value={entry.description}
                          onChange={(e) =>
                            updateCustomBlocklistEntry(idx, { description: e.target.value })
                          }
                          disabled={disabled}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Additional notes */}
            <div>
              <label className="block text-xs text-[var(--text-secondary)] mb-1">
                Additional Notes
              </label>
              <textarea
                className="w-full h-20 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg px-3 py-2 text-sm text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50 resize-none"
                placeholder="Any additional blocklist / match list requirements..."
                value={rulesConfig.customBlocklistNotes || ''}
                onChange={(e) => updateRulesConfig('customBlocklistNotes', e.target.value)}
                disabled={disabled}
              />
            </div>
          </div>
        ) : null}
      </div>
    </>
  )
}
